import axios from './axiosInstance';

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  PayGuard - 결제누락 방지 공통 모듈 (웹)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 *  [배경]
 *   페이팔/페이레터에서는 정상 결제되었으나 그 직후 비토리지 주문서/예치금 저장이 실패하면
 *   (네트워크 순단, 탭 종료, 서버 오류 등) "PG엔 결제됐는데 우리에겐 아무 데이터도 없는"
 *   심각한 누락이 발생한다. 이를 원천 차단하기 위한 공통 유틸이다.
 *
 *  [구성]
 *   1) 결제원장(PAY_ATTEMPT_TB) 기록  : initPayAttempt / notifyPaid / notifyFail / notifyCancel
 *   2) 저장 재시도                    : saveWithRetry (지수 백오프)
 *   3) 이탈 방지                      : createUnloadBlocker
 *   4) 결제식별번호 생성              : generatePayNum
 *
 *  [원칙]
 *   - 결제원장 기록은 어디까지나 '보조 안전장치'다. 기록 실패가 실제 결제/저장을 절대 막지 않는다.
 *     따라서 모든 기록함수는 예외를 삼키고 조용히 실패한다(best-effort).
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 결제원장 용도 코드 */
export const PAY_USAGE = {
    ORDER: '0',        // 주문서 최초작성
    ORDER_MODIFY: '1', // 주문서 수정
    DEPOSIT: '2',      // 예치금 충전
};

/**
 * 결제식별번호 생성 (yyMMdd + 10자리 난수)
 * - 기존 각 결제모달의 generatePayNum / generateMsgNum 과 동일한 규칙을 유지한다.
 */
export const generatePayNum = () => {
    const today = new Date();
    const formatter = new Intl.DateTimeFormat('ko-KR', {year: '2-digit', month: '2-digit', day: '2-digit'});
    const dateString = formatter.format(today).replace(/[^0-9]/g, ''); // yyMMdd
    const randomNumber = Math.floor(Math.random() * 10_000_000_000).toString().padStart(10, '0');
    return dateString + randomNumber;
};

const authHeader = () => {
    const LCAT = window.localStorage.getItem('AToken');
    const SSAT = window.sessionStorage.getItem('AToken');
    return {Authorization: LCAT || SSAT};
};

/**
 * [1] 결제팝업을 열기 직전 결제원장 선기록 (INIT)
 * - 이후 PG에서 결제가 성공하면 서버가 이 행을 PAID 로 갱신한다.
 */
export const initPayAttempt = async ({payNum, payWay, usageType, amount, forAmount, forCurrency}) => {
    try {
        await axios.post('/payAttempt/init', {
            payNum,
            payWay,
            usageType,
            amount: amount != null ? Math.round(Number(String(amount).replace(/,/g, '')) || 0) : null,
            forAmount: forAmount != null ? Number(String(forAmount).replace(/,/g, '')) || 0 : null,
            forCurrency: forCurrency || null,
            platform: 'WEB',
        }, {headers: authHeader()});
    } catch (e) {
        console.log('[PayGuard] 결제원장 INIT 기록 실패(무시하고 결제 진행)', e);
    }
};

/**
 * [2] PG 결제성공 통보 (PAID)
 * @returns {Promise<boolean>} true 면 이미 결제성공으로 기록된 건(=중복). 호출부는 중복 결제처리를 중단해야 한다.
 */
export const notifyPaid = async ({payNum, pgTid}) => {
    try {
        const res = await axios.post('/payAttempt/paid', {payNum, pgTid}, {headers: authHeader()});
        return !!(res && res.data && res.data.duplicated);
    } catch (e) {
        console.log('[PayGuard] 결제원장 PAID 기록 실패(무시하고 저장 진행)', e);
        return false;
    }
};

/** [3] PG 결제성공 후 비토리지 저장 실패 통보 (FAILED) */
export const notifyFail = async ({payNum, failReason, orderNumber}) => {
    try {
        await axios.post('/payAttempt/fail', {payNum, failReason, orderNumber}, {headers: authHeader()});
    } catch (e) {
        console.log('[PayGuard] 결제원장 FAIL 기록 실패', e);
    }
};

/** [4] 사용자 결제취소 / PG 실패 통보 (CANCEL) */
export const notifyCancel = async ({payNum, failReason}) => {
    try {
        await axios.post('/payAttempt/cancel', {payNum, failReason}, {headers: authHeader()});
    } catch (e) {
        console.log('[PayGuard] 결제원장 CANCEL 기록 실패', e);
    }
};

/** 프론트엔드 오류를 ErrorLog 테이블에 남긴다 (누락 원인추적용) */
export const sendErrorLog = async (email, title, content) => {
    try {
        await axios.post('/errorlog/save', {
            email: email || 'WebUser',
            title: `[Web] ${title}`,
            content: typeof content === 'object' ? JSON.stringify(content) : String(content),
        });
    } catch (e) {
        console.log('[PayGuard] ErrorLog 전송 실패', e);
    }
};

/**
 * [5] 저장 재시도 (지수 백오프)
 *
 * - 네트워크 순단/일시적 서버오류로 인한 누락을 막기 위해 최대 attempts 회 재시도한다.
 * - 400번대(요청 자체가 잘못된 경우)는 재시도해도 소용없으므로 즉시 중단한다.
 *
 * @param {Function} requestFn 실제 요청을 수행하는 함수 (Promise 반환)
 * @param {Object}   options   {attempts, baseDelay, onRetry}
 */
export const saveWithRetry = async (requestFn, options = {}) => {
    const attempts = options.attempts || 3;
    const baseDelay = options.baseDelay || 1000;

    let lastError = null;
    for (let i = 0; i < attempts; i++) {
        try {
            return await requestFn();
        } catch (error) {
            lastError = error;
            const status = error?.response?.status;
            // 4xx(인증제외)는 재시도 무의미 -> 즉시 중단
            if (status && status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429) {
                break;
            }
            if (i < attempts - 1) {
                if (options.onRetry) options.onRetry(i + 1, error);
                await new Promise(resolve => setTimeout(resolve, baseDelay * Math.pow(2, i)));
            }
        }
    }
    throw lastError;
};

/**
 * [6] 저장 진행중 페이지 이탈 방지
 *
 * - 결제완료 직후 ~ 주문서 저장완료 사이에 사용자가 탭을 닫으면 데이터가 유실된다.
 * - block() 호출 시점부터 release() 까지 브라우저 기본 이탈확인 창을 띄운다.
 *
 * @returns {{block: Function, release: Function}}
 */
export const createUnloadBlocker = () => {
    const handler = (event) => {
        event.preventDefault();
        event.returnValue = '';
        return '';
    };
    return {
        block: () => window.addEventListener('beforeunload', handler),
        release: () => window.removeEventListener('beforeunload', handler),
    };
};

/* ══════════════════════════════════════════════════════════════════════════════
 *  페이팔 주문상태 판정
 * ══════════════════════════════════════════════════════════════════════════════
 *  PAID     : 돈이 빠졌다고 확정할 수 있는 상태
 *  NOT_PAID : 돈이 빠지지 않았다고 확정할 수 있는 상태
 *  그 외/조회실패 : '모름' — 주문서를 잃지 않도록 결제대기로 저장해두고 관리자가 대사한다.
 * ══════════════════════════════════════════════════════════════════════════════ */
export const PAYPAL_PAID = ['COMPLETED', 'PENDING'];
export const PAYPAL_NOT_PAID = ['CREATED', 'SAVED', 'APPROVED', 'VOIDED', 'PAYER_ACTION_REQUIRED'];

/** 'paid' | 'notPaid' | 'unknown' */
export const judgePaypalStatus = (status) => {
    if (!status) return 'unknown';
    const s = String(status).toUpperCase();
    if (PAYPAL_PAID.indexOf(s) > -1) return 'paid';
    if (PAYPAL_NOT_PAID.indexOf(s) > -1) return 'notPaid';
    return 'unknown';
};

/**
 * 페이팔 주문상태 재확인
 *  capture 응답을 못 받았을 때(타임아웃/순단) 서버를 통해 페이팔에 직접 물어본다.
 *  @param orderId PayPal order id
 *  @returns 상태 문자열. 조회 자체가 실패하면 null(=결제여부 모름)
 */
export const recheckPaypalStatus = async (orderId) => {
    if (!orderId) return null;
    try {
        const res = await axios.post('/externalApi/checkOrderStatus', {orderId});
        return (res && res.data && res.data.status) ? res.data.status : null;
    } catch (e) {
        console.log('[PayGuard] 페이팔 상태 재확인 실패(결제여부 불명)', e);
        return null;
    }
};

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  [7] 결제 후 결과화면의 버튼이 눌리지 않던 문제
 * ════════════════════════════════════════════════════════════════════════════════
 *  [증상]
 *   페이팔 결제가 끝나고 결제완료 화면으로 넘어오면, 버튼이 멀쩡히 보이는데
 *   눌리지도 않고 마우스를 올려도 손모양이 나오지 않았다.
 *   새로고침하면 정상으로 돌아왔다. 웹에서만, 페이팔에서만 생겼다.
 *
 *  [원인 — inert 속성]
 *   페이팔 SDK 는 결제창이 떠 있는 동안 뒤쪽 화면을 못 만지게 하려고
 *   본문에 inert 속성을 건다. 결제창이 닫힐 때 떼어내는데,
 *   그 전에 우리가 팝업을 닫고 다음 화면으로 넘어가면 떼어낼 주체가 사라져
 *   inert 만 남는다. React 라우팅은 그 속성을 건드리지 않으므로 화면만 바뀐다.
 *
 *   inert 가 남아있으면 그 안쪽 전체가
 *     - 클릭/마우스 판정에서 빠지고 (elementFromPoint 가 그 바깥을 돌려준다)
 *     - 커서 모양도 적용되지 않으며
 *     - 화면에는 그대로 보인다
 *
 *   [찾기 어려웠던 이유]
 *    inert 는 CSS 가 아니라 HTML 속성이다.
 *    그래서 getComputedStyle / 스타일 검사로는 아무리 뒤져도 보이지 않는다.
 *    덮개도 없고 pointer-events 도 정상인데 버튼만 안 눌린다면 이것을 의심할 것.
 *
 *  [부수적으로 함께 남던 것]
 *   같은 이유로 화면 전체를 덮는 <div id="paypal-overlay-uid_..."> 도 남을 수 있다.
 *   이쪽은 눈에 보이지 않는 투명한 덮개다.
 *
 *  [방어]
 *   1) 근본 : SDK 뒷정리를 기다린 뒤에 화면을 넘긴다 (PaypalPaymentBt)
 *   2) 보완 : 그래도 남은 inert / 덮개를 걷어낸다 (아래)
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 로그에 적을 요소 설명 */
const describeEl = (el) => {
    try {
        const cls = (typeof el.className === 'string' && el.className.trim())
            ? '.' + el.className.trim().split(/\s+/).join('.')
            : '';
        return `<${el.tagName.toLowerCase()}>${el.id ? '#' + el.id : ''}${cls}`;
    } catch (e) {
        return '<?>';
    }
};

/**
 * 남아있는 inert / aria-hidden 을 걷어낸다.  ← 이번 문제의 실제 해결책
 *  우리 코드는 inert 를 쓰지 않으므로, 붙어있다면 전부 외부에서 남긴 것이다.
 *  aria-hidden 도 함께 정리한다. 남으면 화면 읽어주는 도구에서 본문이 통째로 사라진다.
 */
export const clearInertLeftovers = () => {
    try {
        let fixed = 0;

        document.querySelectorAll('[inert]').forEach((el) => {
            el.removeAttribute('inert');
            try { el.inert = false; } catch (e) { /* 속성만 지워도 된다 */ }
            console.log('[PayGuard] inert 를 떼어냈습니다 ->', describeEl(el));
            fixed += 1;
        });

        const root = document.getElementById('root');
        [root, document.body, document.documentElement].forEach((el) => {
            if (el && el.getAttribute('aria-hidden') === 'true') {
                el.removeAttribute('aria-hidden');
                console.log('[PayGuard] aria-hidden 을 떼어냈습니다 ->', describeEl(el));
                fixed += 1;
            }
        });

        return fixed;
    } catch (e) {
        return 0;
    }
};

/**
 * 페이팔이 <body> 에 남긴 투명한 전면 덮개를 치운다.
 *  결과화면에는 페이팔 UI 가 하나도 없으므로 남아있는 paypal-* 는 전부 찌꺼기다.
 */
const PAYPAL_OVERLAY_SELECTOR = '[id^="paypal-overlay-"], .paypal-checkout-sandbox';

export const sweepPaypalOverlays = () => {
    try {
        const root = document.getElementById('root');
        let removed = 0;
        document.querySelectorAll(PAYPAL_OVERLAY_SELECTOR).forEach((el) => {
            if (root && root.contains(el)) return;    // 우리 화면 안쪽은 건드리지 않는다
            if (!el.parentNode) return;
            console.log('[PayGuard] 페이팔 덮개를 치웠습니다 ->', describeEl(el));
            el.parentNode.removeChild(el);
            removed += 1;
        });
        return removed;
    } catch (e) {
        return 0;
    }
};

/**
 * 페이팔이 잠가둔 스크롤/클릭 잠금을 풀어준다.
 *  SDK 가 결제창을 띄우며 건 잠금이 남는 경우가 있다.
 *  (우리 코드는 html/body 에 이 속성들을 직접 쓰지 않으므로 지워도 안전하다)
 */
export const restoreDocumentInteractivity = () => {
    try {
        [document.documentElement, document.body].forEach((el) => {
            if (!el || !el.style) return;
            if (window.getComputedStyle(el).pointerEvents === 'none') {
                el.style.pointerEvents = '';
                console.log('[PayGuard] 클릭잠금을 풀었습니다 ->', describeEl(el));
            }
            if (el.style.overflow === 'hidden') {
                el.style.overflow = '';
            }
        });
    } catch (e) { /* 청소 실패가 화면을 망가뜨리면 안 된다 */ }
};

/** 남은 찌꺼기를 한 번에 점검한다 */
const sweepAll = () => {
    clearInertLeftovers();          // 이번 문제의 원인
    sweepPaypalOverlays();          // 함께 남던 투명 덮개
    restoreDocumentInteractivity(); // 스크롤/클릭 잠금
};

/**
 * 페이팔 버튼이 사라진 뒤 잠시 동안 찌꺼기를 감시하며 치운다.
 *  결제 후 어디로 이동하든(결제완료 / 결제대기 / 예치금내역 …) 통하도록
 *  '찌꺼기를 남기는 쪽' 에서 직접 치운다.
 *
 *  @param durationMs 감시할 시간 (기본 6초 — 이동과 뒷정리가 끝나기에 충분)
 */
export const watchPaypalOverlays = (durationMs = 6000) => {
    try {
        sweepAll();
        const observer = new MutationObserver(sweepAll);
        observer.observe(document.body, {childList: true, attributes: true, attributeFilter: ['inert']});
        const stop = setTimeout(() => {
            observer.disconnect();
            sweepAll();
        }, durationMs);
        return () => {
            clearTimeout(stop);
            observer.disconnect();
        };
    } catch (e) {
        return () => {};
    }
};

/**
 * 결과화면(결제완료 / 결제대기 / 결제실패) 진입 시 호출한다.
 *  근본 해결은 PaypalPaymentBt 가 'SDK 뒷정리를 기다린 뒤 화면을 넘기는' 것이고,
 *  여기는 그래도 새어나온 찌꺼기를 걷어내는 안전망이다.
 *
 *  @returns 정리 함수 (useEffect 가 그대로 반환하면 된다)
 */
export const ensureResultButtonsClickable = () => {
    sweepAll();

    /*
     * 찌꺼기는 화면이 바뀐 뒤에 뒤늦게 남기도 한다.
     * 사용자가 버튼을 누르는 시점은 알 수 없으므로 잠시 계속 지켜본다.
     * (결과화면은 정적이라 부담이 없고, 10초 뒤 스스로 멈춘다)
     */
    let observer = null;
    try {
        observer = new MutationObserver(sweepAll);
        observer.observe(document.body, {childList: true, attributes: true, attributeFilter: ['inert']});
    } catch (e) { /* 지원하지 않으면 아래 반복점검으로만 동작한다 */ }

    const interval = setInterval(sweepAll, 500);
    const stopInterval = setTimeout(() => clearInterval(interval), 10000);

    return () => {
        clearInterval(interval);
        clearTimeout(stopInterval);
        if (observer) observer.disconnect();
    };
};

/**
 * 확실하게 이동한다.
 *  navigate() 로 주소가 바뀌지 않으면 브라우저 기본 이동으로 넘긴다.
 *  정상일 때는 아무 일도 하지 않는다.
 */
export const navigateSafely = (navigate, path) => {
    const before = window.location.pathname;

    try {
        navigate(path);
    } catch (e) {
        console.warn('[PayGuard] navigate() 가 실패해 기본 이동으로 넘깁니다.', e);
        window.location.assign(path);
        return;
    }

    setTimeout(() => {
        if (window.location.pathname === before && before !== path) {
            console.warn('[PayGuard] navigate() 가 먹히지 않아 기본 이동으로 넘깁니다 ->', path);
            window.location.assign(path);
        }
    }, 150);
};
